import { authService } from "@/api/auth";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/utils/error";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldCheck } from "lucide-react";
import { useState } from "react";

export default function AccountSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const isGoogleAuthUser = user?.authProvider === "google";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!user?.email) {
      setError("Unable to identify current user email.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (!isGoogleAuthUser && !currentPassword) {
      setError("Current password is required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isGoogleAuthUser) {
        await authService.setLocalPassword(newPassword);
      } else {
        await authService.changePassword({
          email: user.email,
          oldPassword: currentPassword,
          newPassword,
        });
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      const successText = isGoogleAuthUser
        ? "Password set successfully. You can now sign in with email and password in addition to Google."
        : "Password changed successfully.";
      setSuccessMessage(successText);
      toast({
        title: isGoogleAuthUser ? "Password set" : "Password changed",
        description: isGoogleAuthUser
          ? "You can now sign in with email and password in addition to Google."
          : "Your password has been updated successfully.",
      });
    } catch (err) {
      const apiError = err as ApiError;
      const message =
        apiError?.response?.data?.error?.message ||
        apiError?.message ||
        `Failed to ${isGoogleAuthUser ? "set" : "change"} password. Please try again.`;
      setError(message);
      setSuccessMessage("");
      toast({
        title: `Unable to ${isGoogleAuthUser ? "set" : "change"} password`,
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Account Settings</h2>
          <p className="text-muted-foreground mt-1">
            Manage login and security preferences for your account.
          </p>
        </div>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              {isGoogleAuthUser ? "Set Password" : "Change Password"}
            </CardTitle>
            <CardDescription>
              {isGoogleAuthUser
                ? "If you signed in with Google first, set a local password to enable email and password login as well."
                : "Change your existing password by entering your current password first."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {successMessage && (
                <Alert className="border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-300">
                  <AlertDescription>{successMessage}</AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert variant="destructive" className="border-red-500/50 bg-red-500/10 text-red-700 dark:text-red-300">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {!isGoogleAuthUser && (
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  minLength={8}
                  disabled={isSubmitting}
                />
              </div>

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isGoogleAuthUser ? "Set Password" : "Change Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
